import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { Observable } from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators'

import { AAAStore } from '../../store/root-reducer'
import { endSecureAuth } from '../auth/auth.actions'
import { AdobeEventService } from '../tagging/adobe/event-adobe.service'
import auth from '../tagging/events/auth'
import { AdobeEventTypes } from '../tagging/tagging.types'
import { resetLoading } from '../ui/loading/loading.actions'
import { openErrorDialog } from '../ui/ui.actions'
import { ErrorDialogTypes } from '../ui/ui.types'
import {
  MEMBER_ELIGIBILITY_RESULT,
  MEMBER_INFO,
  memberInfoBackgroundPoll,
  QUICK_RENEW_CHECK_TIMED_OUT,
  QUICK_RENEW_FINAL_CHECK,
  quickRenewCheckTimedOut,
  quickRenewFinalCheck,
  SET_QUICK_RENEW_RETURN,
} from './member.actions'
import {
  selectIsPaymentInProcessSheetOpen,
  selectIsQuickRenewResolving,
  selectQuickRenewReturn,
} from './member.selectors'
import { QuickRenewPaymentStatusService } from './quick-renew-payment-status.service'

const QUICK_RENEW_TIMEOUT_MESSAGE = () =>
  $localize`We received your payment, but your membership activation is taking longer than expected.`
const QUICK_RENEW_TIMEOUT_ASSISTANCE = () =>
  $localize`For additional assistance, please call AAA`

@Injectable()
export class MemberQuickRenewEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<AAAStore>,
    private quickRenewPaymentStatusService: QuickRenewPaymentStatusService,
    private adobeEventService: AdobeEventService
  ) {}

  // The sheet is opened by the reducer on MEMBER_INFO.SUCCESS, and that action
  // re-fires on every 30s poll while the sheet stays open — an action-based
  // withLatestFrom would emit duplicates, so this watches the flag's
  // false->true transition instead.
  paymentInProcessSheetShown$ = createEffect(
    () =>
      this.store$.select(selectIsPaymentInProcessSheetOpen).pipe(
        distinctUntilChanged(),
        filter(Boolean),
        tap(() =>
          this.adobeEventService.sendEvent({
            eventName: AdobeEventTypes.SYSTEM,
            eventValue: auth.PAYMENT_IN_PROCESS_SHEET_SHOWN,
          })
        )
      ),
    { dispatch: false }
  )

  startQuickRenewPolling$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SET_QUICK_RENEW_RETURN),
        map(() => this.quickRenewPaymentStatusService.start())
      ),
    { dispatch: false }
  )

  // The poll only ever stops itself: on a successful resolve (below) or on
  // the overall timeout (quickRenewCheckTimedOut$).
  stopQuickRenewPollingOnResolve$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MEMBER_INFO.SUCCESS),
        withLatestFrom(this.store$.select(selectIsQuickRenewResolving)),
        filter(([_, resolving]: [unknown, boolean]) => resolving),
        map(() => this.quickRenewPaymentStatusService.stop())
      ),
    { dispatch: false }
  )

  // The call-status flow short-circuits to memberEligibilityResult for members
  // still in the renew state, so CALL_STATUS.SUCCESS never fires and its loading
  // flag stays set — leaving the login form's Confirm button spinning once the
  // payment-in-process sheet closes. Clear it, like openPendingGraceSheet$ does
  // for the pending-grace branch.
  resetLoadingOnQuickRenewEligibilityResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MEMBER_ELIGIBILITY_RESULT),
      withLatestFrom(this.store$.select(selectQuickRenewReturn)),
      filter(([_, quickRenewReturn]) => Boolean(quickRenewReturn)),
      map(() => resetLoading())
    )
  )

  // The splashscreen never auto-hides while isSecure is true, so without this
  // the member dismisses the "call AAA" dialog into a permanent spinner
  // instead of the login form.
  quickRenewCheckTimedOut$ = createEffect(
    (): Observable<
      | ReturnType<typeof endSecureAuth>
      | ReturnType<typeof openErrorDialog>
    > =>
      this.actions$.pipe(
        ofType(QUICK_RENEW_CHECK_TIMED_OUT),
        switchMap(() => {
          this.quickRenewPaymentStatusService.stop()
          this.adobeEventService.sendEvent({
            eventName: AdobeEventTypes.SYSTEM,
            eventValue: auth.QUICK_RENEW_TIMEOUT_PROMPT,
          })
          return [
            endSecureAuth(),
            openErrorDialog({
              payload: {
                type: ErrorDialogTypes.GENERIC,
                content: `<p>${QUICK_RENEW_TIMEOUT_MESSAGE()}<br/> ${QUICK_RENEW_TIMEOUT_ASSISTANCE()} </p>`,
              },
            }),
          ]
        })
      )
  )

  // The 3-minute overall timeout (quick-renew-payment-status.service.ts)
  // dispatches this instead of timing out directly, so the member gets one
  // last member-details recheck before we give up on them.
  finalQuickRenewCheck$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QUICK_RENEW_FINAL_CHECK),
      map(() => memberInfoBackgroundPoll())
    )
  )

  // A response to the final check is guaranteed to settle MEMBER_INFO.SUCCESS
  // or .FAILURE — the HTTP timeout interceptor kills a hung request at 30s,
  // which loadMemberInfo$ maps to MEMBER_INFO.FAILURE. Only fire the timeout
  // dialog if that settle didn't resolve the payment.
  quickRenewTimeoutAfterFinalCheck$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QUICK_RENEW_FINAL_CHECK),
      switchMap(() =>
        this.actions$.pipe(
          ofType(MEMBER_INFO.SUCCESS, MEMBER_INFO.FAILURE),
          take(1),
          withLatestFrom(this.store$.select(selectIsQuickRenewResolving)),
          filter(([_, resolving]: [unknown, boolean]) => !resolving),
          map(() => quickRenewCheckTimedOut())
        )
      )
    )
  )
}
